/**
 * svgSilhouette — pure SVG-path → filled-polygon geometry for authored parallax
 * silhouettes (Coil art pack, Lane D).
 *
 * Pixi's IGraphics has no native SVG-path renderer, but it DOES have
 * `fillPolygon`. So: parse the SVG `d` string, flatten curves into line
 * segments, and hand back one polygon per subpath — the parallax effect then
 * issues plain `fillPolygon` calls. Silhouettes are solid fills (no stroke),
 * which is exactly what a polygon fill gives us.
 *
 * Supported commands: M/m L/l H/h V/v C/c S/s Q/q T/t Z/z. Arcs (A/a) are
 * deliberately unsupported — author with beziers. Pure math (no Pixi) so the
 * parser + band layout are unit-testable.
 */
import type {
  ParallaxSvgSpec,
  ParallaxSvgFeature,
} from '../../../../config/atmosphere/effectLayer.types';

export interface Point { x: number; y: number; }

/** One solid fill instruction for the parallax renderer (already in band-local px). */
export interface SilhouetteFill {
  points: Point[];
  color: number;
  alpha: number;
}

/** Segments used to flatten each cubic/quadratic bezier. Background silhouettes
 *  at band scale — 14 is visually smooth and cheap (drawn once at mount). */
const CURVE_SEGMENTS = 14;

const TOKEN_RE = /([A-Za-z])|(-?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?)/g;

function tokenize(d: string): (string | number)[] {
  const tokens: (string | number)[] = [];
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(d)) !== null) {
    if (m[1]) tokens.push(m[1]);
    else tokens.push(parseFloat(m[2]));
  }
  return tokens;
}

function cubicAt(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
}

function quadAt(p0: number, p1: number, p2: number, t: number): number {
  const u = 1 - t;
  return u * u * p0 + 2 * u * t * p1 + t * t * p2;
}

/**
 * Parse + flatten an SVG path `d` into closed polygons (one per subpath).
 * Every subpath is treated as closed for filling (SVG fill semantics).
 * Throws on unsupported commands so authoring mistakes surface in tests.
 */
export function svgPathToPolygons(d: string, curveSegments = CURVE_SEGMENTS): Point[][] {
  const tokens = tokenize(d);
  const polygons: Point[][] = [];
  let current: Point[] = [];
  let cx = 0; let cy = 0;           // pen
  let sx = 0; let sy = 0;           // subpath start
  let pcx: number | null = null;    // previous cubic control (for S)
  let pcy: number | null = null;
  let pqx: number | null = null;    // previous quadratic control (for T)
  let pqy: number | null = null;
  let cmd = '';
  let i = 0;

  const flush = () => {
    if (current.length >= 3) polygons.push(current);
    current = [];
  };
  const num = (): number => {
    const t = tokens[i];
    if (typeof t !== 'number') throw new Error(`[svgSilhouette] expected number at token ${i} (cmd '${cmd}')`);
    i += 1;
    return t;
  };
  const push = (x: number, y: number) => { current.push({ x, y }); };
  const clearCtrl = () => { pcx = pcy = pqx = pqy = null; };

  while (i < tokens.length) {
    const t = tokens[i];
    if (typeof t === 'string') { cmd = t; i += 1; }
    // else: implicit repeat of the previous command (M/m repeats as L/l per spec)
    else if (cmd === 'M') cmd = 'L';
    else if (cmd === 'm') cmd = 'l';

    switch (cmd) {
      case 'M': case 'm': {
        const x = num(); const y = num();
        flush();
        cx = cmd === 'm' ? cx + x : x;
        cy = cmd === 'm' ? cy + y : y;
        sx = cx; sy = cy;
        push(cx, cy);
        clearCtrl();
        break;
      }
      case 'L': case 'l': {
        const x = num(); const y = num();
        cx = cmd === 'l' ? cx + x : x;
        cy = cmd === 'l' ? cy + y : y;
        push(cx, cy);
        clearCtrl();
        break;
      }
      case 'H': case 'h': {
        const x = num();
        cx = cmd === 'h' ? cx + x : x;
        push(cx, cy);
        clearCtrl();
        break;
      }
      case 'V': case 'v': {
        const y = num();
        cy = cmd === 'v' ? cy + y : y;
        push(cx, cy);
        clearCtrl();
        break;
      }
      case 'C': case 'c': case 'S': case 's': {
        let x1: number; let y1: number;
        if (cmd === 'C' || cmd === 'c') {
          x1 = num(); y1 = num();
          if (cmd === 'c') { x1 += cx; y1 += cy; }
        } else {
          // S: first control = reflection of previous cubic control (or pen)
          x1 = pcx !== null ? 2 * cx - pcx : cx;
          y1 = pcy !== null ? 2 * cy - (pcy as number) : cy;
        }
        let x2 = num(); let y2 = num();
        let x = num(); let y = num();
        if (cmd === 'c' || cmd === 's') { x2 += cx; y2 += cy; x += cx; y += cy; }
        for (let k = 1; k <= curveSegments; k += 1) {
          const tt = k / curveSegments;
          push(cubicAt(cx, x1, x2, x, tt), cubicAt(cy, y1, y2, y, tt));
        }
        cx = x; cy = y;
        pcx = x2; pcy = y2; pqx = pqy = null;
        break;
      }
      case 'Q': case 'q': case 'T': case 't': {
        let x1: number; let y1: number;
        if (cmd === 'Q' || cmd === 'q') {
          x1 = num(); y1 = num();
          if (cmd === 'q') { x1 += cx; y1 += cy; }
        } else {
          x1 = pqx !== null ? 2 * cx - pqx : cx;
          y1 = pqy !== null ? 2 * cy - (pqy as number) : cy;
        }
        let x = num(); let y = num();
        if (cmd === 'q' || cmd === 't') { x += cx; y += cy; }
        for (let k = 1; k <= curveSegments; k += 1) {
          const tt = k / curveSegments;
          push(quadAt(cx, x1, x, tt), quadAt(cy, y1, y, tt));
        }
        cx = x; cy = y;
        pqx = x1; pqy = y1; pcx = pcy = null;
        break;
      }
      case 'Z': case 'z': {
        cx = sx; cy = sy;
        flush();
        clearCtrl();
        break;
      }
      default:
        throw new Error(`[svgSilhouette] unsupported SVG path command '${cmd}' — author with M/L/H/V/C/S/Q/T/Z`);
    }
  }
  flush();
  return polygons;
}

/** Uniform scale + translate. */
export function transformPolygons(polys: Point[][], scale: number, dx: number, dy: number): Point[][] {
  return polys.map((poly) => poly.map((p) => ({ x: p.x * scale + dx, y: p.y * scale + dy })));
}

/** Band geometry the parallax effect computes at mount. */
export interface SilhouetteBand {
  /** Full band width (view + 2×margin), px. */
  width: number;
  /** Plane height (layer.height), px. */
  height: number;
  /** View width, px (feature anchorX is a fraction of this). */
  viewW: number;
  /** Left slack before the view starts, px (band-local x of screen-left at camera 0). */
  margin: number;
  /** Feature anchor shift, px — the parallax offset of the MOUNT camera
   *  (`cameraLeft × layer.speed`). Spawn cameras sit at arbitrary world x; this
   *  keeps the landmark at `anchorX` of the view the player actually sees at
   *  mount (it then parallax-scrolls naturally as they move). Default 0. */
  featureOffsetPx?: number;
}

/**
 * Repeating-terrain fills only (camera-independent): tile repeats, then tile
 * glow repeats ON TOP (cracks / gap shafts must read over the stone).
 */
export function svgTileFills(
  tile: ParallaxSvgSpec['tile'],
  band: SilhouetteBand,
  base: { color: number; alpha: number },
): SilhouetteFill[] {
  const fills: SilhouetteFill[] = [];
  const tileScale = band.height / tile.viewBoxHeight;
  const tileW = tile.viewBoxWidth * tileScale;
  const repeats = tileW > 0 ? Math.max(1, Math.ceil(band.width / tileW)) : 0;
  const tilePolys = svgPathToPolygons(tile.path);
  const glowPolys = tile.glowPath ? svgPathToPolygons(tile.glowPath) : null;
  for (let r = 0; r < repeats; r += 1) {
    for (const poly of transformPolygons(tilePolys, tileScale, r * tileW, 0)) {
      fills.push({ points: poly, color: base.color, alpha: base.alpha });
    }
  }
  if (glowPolys) {
    for (let r = 0; r < repeats; r += 1) {
      for (const poly of transformPolygons(glowPolys, tileScale, r * tileW, 0)) {
        fills.push({ points: poly, color: tile.glowColor ?? 0x00ff7f, alpha: tile.glowAlpha ?? 0.15 });
      }
    }
  }
  return fills;
}

/**
 * One-shot landmark fills (glow halo → fill). Camera-DEPENDENT: the renderer
 * bakes `band.featureOffsetPx` from the first live camera so the landmark
 * mounts at `anchorX` of the view the player actually sees.
 */
export function svgFeatureFills(
  f: ParallaxSvgFeature,
  band: SilhouetteBand,
  base: { color: number; alpha: number },
): SilhouetteFill[] {
  const out: SilhouetteFill[] = [];
  const scale = f.heightPx / f.viewBoxHeight;
  const featureW = f.viewBoxWidth * scale;
  const dx = band.margin + f.anchorX * band.viewW + (band.featureOffsetPx ?? 0) - featureW / 2;
  const dy = band.height - (f.liftPx ?? 0) - f.heightPx;
  if (f.glowPath) {
    for (const poly of transformPolygons(svgPathToPolygons(f.glowPath), scale, dx, dy)) {
      out.push({ points: poly, color: f.glowColor ?? 0x00ff7f, alpha: f.glowAlpha ?? 0.1 });
    }
  }
  for (const poly of transformPolygons(svgPathToPolygons(f.path), scale, dx, dy)) {
    out.push({ points: poly, color: f.color ?? base.color, alpha: f.alpha ?? base.alpha });
  }
  return out;
}

/**
 * Resolve an authored ParallaxSvgSpec into ordered fill instructions in
 * band-local px ([0..width] × [0..height], y down; features may extend above
 * y 0 — they poke over the band top by design).
 *
 * Order: behind-tile feature (glow → fill) → tile repeats → tile glow repeats
 * → in-front feature (glow → fill).
 */
export function svgSilhouetteFills(
  svg: ParallaxSvgSpec,
  band: SilhouetteBand,
  base: { color: number; alpha: number },
): SilhouetteFill[] {
  const fills: SilhouetteFill[] = [];
  const { tile, feature } = svg;
  if (feature && feature.order === 'behindTile') fills.push(...svgFeatureFills(feature, band, base));
  fills.push(...svgTileFills(tile, band, base));
  if (feature && feature.order !== 'behindTile') fills.push(...svgFeatureFills(feature, band, base));
  return fills;
}
